import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { track } from "../lib/track";

const API_URL = import.meta.env.VITE_API_URL as string;

interface SquareConfig {
  environment: "sandbox" | "production";
  application_id: string;
  location_id: string;
  monthly_plan_id: string;
  yearly_plan_id: string;
  trial_days: number;
}

interface Props {
  onBack?: () => void;
  onSuccess?: () => void;
}

export default function UpgradeScreen({ onBack, onSuccess }: Props) {
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly");
  const [sqReady, setSqReady] = useState(false);
  const [sqInitErr, setSqInitErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [trialDays, setTrialDays] = useState(7);
  const cardRef = useRef<any>(null);
  const initDoneRef = useRef(false);

  useEffect(() => {
    // initDoneRef guards against StrictMode's double-invoke. Do NOT reset it in the cleanup
    // return — that would allow the second StrictMode effect to re-run loadSquare and produce
    // duplicate forms. Instead, we destroy the card instance so any stale Square iframe from
    // an HMR hot-reload or rapid mount/unmount is cleaned up before the new one attaches.
    if (initDoneRef.current) return;
    loadSquare();
    return () => {
      if (cardRef.current?.destroy) {
        cardRef.current.destroy().catch(() => {});
        cardRef.current = null;
      }
    };
  }, []);

  async function loadSquare() {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    let cfg: SquareConfig;
    try {
      const r = await fetch(`${API_URL}/api/square/config`);
      const json = await r.json();
      if (!json.ok) throw new Error(json.error ?? "config error");
      cfg = json as SquareConfig;
      setTrialDays(cfg.trial_days ?? 7);
    } catch {
      initDoneRef.current = false;
      setSqInitErr(true);
      return;
    }

    // Derive env from application_id prefix — more robust than trusting the environment field.
    // Square sandbox app IDs always start with "sandbox-"; production IDs do not.
    const isSandbox = cfg.application_id.startsWith("sandbox-");
    const sdkUrl = isSandbox
      ? "https://sandbox.web.squarecdn.com/v1/square.js"
      : "https://web.squarecdn.com/v1/square.js";

    if ((window as any).Square) {
      initSquare(cfg);
      return;
    }
    const script = document.createElement("script");
    script.src = sdkUrl;
    script.onload = () => initSquare(cfg);
    script.onerror = () => setSqInitErr(true);
    document.head.appendChild(script);
  }

  async function initSquare(cfg: SquareConfig) {
    try {
      const payments = (window as any).Square.payments(cfg.application_id, cfg.location_id);
      const card = await payments.card();
      await card.attach("#sq-card-container");
      cardRef.current = card;
      setSqReady(true);
    } catch {
      initDoneRef.current = false;
      setSqInitErr(true);
    }
  }

  async function handleSubscribe() {
    if (!cardRef.current || busy) return;
    setError(null);
    setBusy(true);
    track("pro_upgrade_started", { plan: selected });
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") {
        setError(result.errors?.[0]?.message ?? "Card error. Please check your details.");
        setBusy(false);
        return;
      }
      if (!supabase) { setError("Auth unavailable."); setBusy(false); return; }
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setError("Please sign in first."); setBusy(false); return; }

      const res = await fetch(`${API_URL}/api/square/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ source_id: result.token, plan: selected }),
      });
      const data = await res.json();
      if (data.ok) {
        track("pro_subscribed", { plan: selected });
        setSuccess(true);
      } else {
        setError(data.error || "Subscription failed. Please try again.");
        setBusy(false);
      }
    } catch {
      setError("Network error. Please try again.");
      setBusy(false);
    }
  }

  if (success) {
    return (
      <div style={{ fontFamily: "Nunito, system-ui, sans-serif", background: "#0f172a", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", textAlign: "center" }}>
        <div style={{ maxWidth: 360, width: "100%" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One", marginBottom: 10 }}>
            You're in!
          </div>
          <div style={{ fontSize: 15, color: "#94a3b8", fontWeight: 700, lineHeight: 1.6, marginBottom: 28 }}>
            Welcome to MoneyMission Pro. Your {trialDays}-day free trial is now active — let's get to work.
          </div>
          <button
            onClick={onSuccess ?? onBack}
            style={{ width: "100%", padding: "16px", fontSize: 16, fontWeight: 900, background: "linear-gradient(135deg,#0d9488,#f59e0b)", color: "#fff", border: "none", borderRadius: 14, cursor: "pointer", fontFamily: "Nunito, system-ui, sans-serif", minHeight: 52 }}
          >
            Enter MoneyMission Pro →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Nunito, system-ui, sans-serif", background: "#0f172a", minHeight: "100vh", paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#134e4a,#0f172a)", padding: "32px 20px 24px", textAlign: "center", position: "relative" }}>
        {onBack && (
          <button onClick={onBack} style={{ position: "absolute", top: 16, left: 16, background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 20, padding: "8px 14px", color: "#94a3b8", fontWeight: 700, fontSize: 13, cursor: "pointer", minHeight: 44 }}>
            ← Back
          </button>
        )}
        <div style={{ fontSize: 40, marginBottom: 8 }}>🚀</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One", marginBottom: 6 }}>
          MoneyMission Pro
        </div>
        <div style={{ fontSize: 14, color: "#94a3b8", fontWeight: 700, lineHeight: 1.5, maxWidth: 300, margin: "0 auto" }}>
          Real-world financial skills for your 20s — portfolio, taxes, career, and more.
        </div>
        <div style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 20, padding: "6px 16px" }}>
          <span style={{ fontSize: 16 }}>🎁</span>
          <span style={{ fontSize: 13, fontWeight: 900, color: "#f59e0b" }}>{trialDays}-day free trial — no charge today</span>
        </div>
      </div>

      {/* Feature highlights */}
      <div style={{ padding: "20px 20px 0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { e: "📚", label: "180+ Pro Lessons" },
            { e: "🤖", label: "AI MoneyBot" },
            { e: "📊", label: "Budget Tracker" },
            { e: "🎯", label: "Personal Plan" },
          ].map(f => (
            <div key={f.label} style={{ background: "#1e293b", borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>{f.e}</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#cbd5e1" }}>{f.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Plan selector */}
      <div style={{ padding: "20px 20px 0" }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", textTransform: "uppercase", letterSpacing: 2, marginBottom: 12, textAlign: "center" }}>
          Choose your plan
        </div>

        <button
          onClick={() => setSelected("yearly")}
          style={{
            width: "100%", textAlign: "left", cursor: "pointer", marginBottom: 10,
            background: selected === "yearly" ? "linear-gradient(135deg,#134e4a,#1e3a5f)" : "#1e293b",
            border: selected === "yearly" ? "2px solid #0d9488" : "2px solid #334155",
            borderRadius: 16, padding: "16px", transition: "all 0.2s", display: "block",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 900, color: "#f1f5f9" }}>Annual Plan</span>
                <span style={{ fontSize: 10, fontWeight: 900, background: "#f59e0b", color: "#0f172a", borderRadius: 6, padding: "2px 7px" }}>BEST VALUE</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#0d9488", fontFamily: "Fredoka One" }}>$59.99<span style={{ fontSize: 13, color: "#64748b" }}>/yr</span></div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>Just $5.00/month — save 29%</div>
            </div>
            <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${selected === "yearly" ? "#0d9488" : "#475569"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
              {selected === "yearly" && <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#0d9488" }} />}
            </div>
          </div>
        </button>

        <button
          onClick={() => setSelected("monthly")}
          style={{
            width: "100%", textAlign: "left", cursor: "pointer",
            background: selected === "monthly" ? "linear-gradient(135deg,#134e4a,#1e3a5f)" : "#1e293b",
            border: selected === "monthly" ? "2px solid #0d9488" : "2px solid #334155",
            borderRadius: 16, padding: "16px", transition: "all 0.2s", display: "block",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#f1f5f9", marginBottom: 4 }}>Monthly Plan</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#0d9488", fontFamily: "Fredoka One" }}>$6.99<span style={{ fontSize: 13, color: "#64748b" }}>/mo</span></div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>Cancel anytime</div>
            </div>
            <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${selected === "monthly" ? "#0d9488" : "#475569"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
              {selected === "monthly" && <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#0d9488" }} />}
            </div>
          </div>
        </button>
      </div>

      {/* Card form */}
      <div style={{ padding: "20px 20px 0" }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", textTransform: "uppercase", letterSpacing: 2, marginBottom: 12, textAlign: "center" }}>
          Payment details
        </div>
        {sqInitErr ? (
          <div style={{ background: "#fee2e2", borderRadius: 12, padding: "12px 16px", color: "#991b1b", fontSize: 13, fontWeight: 700, marginBottom: 12, textAlign: "center" }}>
            Couldn't load payment form. Please refresh and try again.
          </div>
        ) : (
          <div style={{ background: "#1e293b", borderRadius: 12, padding: "16px", marginBottom: 4 }}>
            {!sqReady && (
              <div style={{ textAlign: "center", color: "#475569", fontSize: 13, fontWeight: 700, padding: "12px 0" }}>
                Loading payment form…
              </div>
            )}
            {/* Container must never be display:none while card.attach() runs —
                Square re-initializes when a hidden container becomes visible,
                producing a duplicate iframe. */}
            <div id="sq-card-container" />
          </div>
        )}
      </div>

      {/* CTA */}
      <div style={{ padding: "12px 20px 0" }}>
        {error && (
          <div style={{ background: "#fee2e2", borderRadius: 12, padding: "10px 14px", color: "#991b1b", fontSize: 13, fontWeight: 700, marginBottom: 12, borderLeft: "4px solid #ef4444" }}>
            {error}
          </div>
        )}
        <button
          disabled={!sqReady || busy || sqInitErr}
          onClick={handleSubscribe}
          style={{
            width: "100%", padding: "16px", fontSize: 16, fontWeight: 900,
            background: (!sqReady || busy || sqInitErr) ? "#334155" : "linear-gradient(135deg,#0d9488,#f59e0b)",
            color: "#fff", border: "none", borderRadius: 14,
            cursor: (!sqReady || busy || sqInitErr) ? "not-allowed" : "pointer",
            fontFamily: "Nunito, system-ui, sans-serif", minHeight: 52, transition: "all 0.2s",
          }}
        >
          {busy ? "Processing…" : !sqReady && !sqInitErr ? "Loading…" : `Start ${trialDays}-day free trial`}
        </button>
        <div style={{ fontSize: 11, color: "#475569", textAlign: "center", marginTop: 10, lineHeight: 1.5, fontWeight: 700 }}>
          7-day free trial, then $6.99/mo (or $59.99/yr) — card required, first charge after the trial. Cancel anytime.
        </div>
      </div>
    </div>
  );
}
